// 1. React imports
import { useEffect, useState } from 'react';

// 2. External libraries
import { CircleCheck, Info } from 'lucide-react';

// 3. Internal components
import { Alert, AlertDescription } from '~/components/ui/alert';
import { Card, CardContent } from '~/components/ui/card';
import { Spinner } from '~/components/ui/spinner';
import { WizardActions } from '~/components/onboarding/WizardActions';

// 4. Type imports
import type { AnyWebSocketEvent } from '~/types/websocket';
import { isOnboardingMessageSentEvent } from '~/types/websocket';

// 5. Hooks and utilities
import { getTelegramBotName } from '~/config';
import { useWebSocketSubscription } from '~/hooks/useWebSocketSubscription';

const SUCCESS_DISPLAY_MS = 1500; // 1.5s success display

/**
 * Props for SendTelegramMessageStep component.
 *
 * Per §TS.3.2: Descriptive props interface naming.
 */
export interface SendTelegramMessageStepProps {
  topicId: string;
  onMessageReceived: (messageId: string, content: string) => void;
  onBack: () => void;
  onSkip: () => void;
  showConnectionSuccess?: boolean;
}

/**
 * Message state tracking for send message step.
 *
 * - waiting: User has not sent message yet
 * - success: Message confirmed via WebSocket event
 */
type MessageState = 'waiting' | 'success';

/**
 * Received message data structure.
 */
interface ReceivedMessage {
  id: string;
  content: string;
}

/**
 * Step for sending a message to Telegram bot during onboarding wizard.
 *
 * Displays instructions to send any message to @DeadDropBot and waits for
 * onboarding.message_sent WebSocket event. Shows message preview and auto-advances
 * after 1.5s success display.
 *
 * Per §TS.3.1: Function declaration for named export.
 * Per §TS.2.3: WebSocket event type guards for safe event handling.
 *
 * @example
 * ```tsx
 * <SendTelegramMessageStep
 *   topicId="topic_123"
 *   onMessageReceived={(messageId, content) => handleMessage(messageId, content)}
 *   onBack={() => setStep(2)}
 *   onSkip={handleSkip}
 * />
 * ```
 */
export function SendTelegramMessageStep({
  topicId,
  onMessageReceived,
  onBack,
  onSkip,
  showConnectionSuccess = false,
}: SendTelegramMessageStepProps) {
  const telegramBotName = getTelegramBotName();
  const [messageState, setMessageState] = useState<MessageState>('waiting');
  const [receivedMessage, setReceivedMessage] = useState<ReceivedMessage | null>(null);

  // Listen for WebSocket events using subscription (no new connection)
  useWebSocketSubscription((event: AnyWebSocketEvent) => {
    // Filter events by type and topicId match (per spec)
    // Uses onboarding.message_sent event which includes contentPreview
    if (isOnboardingMessageSentEvent(event) && event.data.topicId === topicId) {
      setReceivedMessage({ id: event.data.messageId, content: event.data.contentPreview });
      setMessageState('success');
    }
  });

  // Auto-advance after success display period
  useEffect(() => {
    if (messageState === 'success' && receivedMessage) {
      const advanceTimeoutId = setTimeout(() => {
        onMessageReceived(receivedMessage.id, receivedMessage.content);
      }, SUCCESS_DISPLAY_MS);

      return () => clearTimeout(advanceTimeoutId);
    }
  }, [messageState, receivedMessage, onMessageReceived]);

  return (
    <div>
      {/* Success Alert - Telegram Connection Success from Step 4 */}
      {showConnectionSuccess && (
        <Alert variant="success" className="mb-6">
          <CircleCheck />
          <AlertDescription>
            Telegram connected! Your bot is ready to receive messages.
          </AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <p className="mb-6 text-sm text-muted-foreground">
        Send any message to the Telegram bot to test the connection.
      </p>

      {/* Success State: Message Preview */}
      {messageState === 'success' && receivedMessage && (
        <Card className="mb-6 border-l-4 border-l-success shadow-[0_0_16px_color-mix(in_srgb,var(--color-success)_20%,transparent)]">
          <CardContent>
            <div className="flex items-center gap-4">
              <CircleCheck className="size-8 text-success" />
              <div>
                <p className="text-sm font-medium">Message Received!</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {receivedMessage.content.length > 100
                    ? receivedMessage.content.slice(0, 100) + '...'
                    : receivedMessage.content}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Waiting State: Instructions */}
      {messageState === 'waiting' && (
        <>
          <Alert className="mb-6">
            <Info className="text-primary" />
            <AlertDescription>Send any message to @{telegramBotName} in Telegram</AlertDescription>
          </Alert>

          {/* Waiting Indicator */}
          <div className="mb-6 flex items-center gap-4 rounded border border-border bg-card p-4">
            <Spinner className="size-6" data-testid="send-message-waiting-spinner" />
            <p className="text-sm text-muted-foreground">Waiting for your message...</p>
          </div>
        </>
      )}

      {/* Wizard Actions */}
      <WizardActions
        onBack={onBack}
        onSkip={onSkip}
        showBack={messageState === 'waiting'}
        isLoading={messageState === 'success'}
      />
    </div>
  );
}
